import { useState, useEffect } from "react";
import {
  List,
  useListContext,
  useDataProvider,
  useDelete,
  useRedirect,
  useCreatePath,
  usePermissions,
  useNotify,
  CreateButton,
  TopToolbar,
  Confirm,
} from "react-admin";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Typography,
  Link,
  IconButton,
  Tooltip,
  CircularProgress,
  Divider,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { alpha } from "@mui/material/styles";

interface ProjectRecord {
  id: string | number;
  name: string;
  description?: string;
  created_at?: string;
}

interface ModelRecord {
  id: string | number;
  name: string;
  description?: string;
  created_at?: string;
}

// Generic, non-leaking message — the API deletes R2 files before the DB row
// (see models.service.ts deleteAreaR2Objects) and fails the whole request if
// storage cleanup can't be verified, so a failure here means nothing was
// deleted and the safest guidance is simply to retry later.
const DELETE_ERROR_MESSAGE = "Eliminazione fallita. Riprova più tardi.";

function formatDate(dateStr?: string): string {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("it-IT", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function ModelRow({
  model,
  onDeleted,
}: {
  model: ModelRecord;
  onDeleted: () => void;
}) {
  const redirect = useRedirect();
  const createPath = useCreatePath();
  const [deleteOne] = useDelete();
  const notify = useNotify();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const editPath = createPath({ resource: "models", type: "edit", id: model.id });

  function handleEdit(e: React.MouseEvent) {
    e.stopPropagation();
    redirect("edit", "models", model.id);
  }

  function handleDeleteClick(e: React.MouseEvent) {
    e.stopPropagation();
    setConfirmOpen(true);
  }

  function handleConfirmDelete() {
    deleteOne(
      "models",
      { id: model.id, previousData: model },
      {
        onSuccess: () => {
          notify("Modello eliminato", { type: "success" });
          onDeleted();
        },
        onError: () => {
          notify(DELETE_ERROR_MESSAGE, { type: "error" });
        },
      }
    );
    setConfirmOpen(false);
  }

  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          px: 2,
          py: 1.25,
          borderBottom: "1px solid",
          borderColor: (theme) => alpha(theme.palette.divider, 0.5),
          "&:last-child": { borderBottom: "none" },
          "&:hover": {
            backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.06),
          },
        }}
      >
        <Box sx={{ flex: "0 0 240px", minWidth: 0 }}>
          <Link
            component={RouterLink}
            to={editPath}
            variant="body2"
            noWrap
            underline="hover"
            sx={{ display: "block", fontWeight: 500 }}
          >
            {model.name}
          </Link>
        </Box>
        <Box sx={{ flex: 1, minWidth: 0, px: 2 }}>
          <Typography variant="body2" color="text.secondary" noWrap>
            {model.description ?? "—"}
          </Typography>
        </Box>
        <Box sx={{ flex: "0 0 120px" }}>
          <Typography variant="body2" color="text.secondary">
            {formatDate(model.created_at)}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 0.5 }}>
          <Tooltip title="Modifica modello">
            <IconButton aria-label="Modifica modello" size="small" onClick={handleEdit}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Elimina modello">
            <IconButton aria-label="Elimina modello" size="small" onClick={handleDeleteClick}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Confirm
        isOpen={confirmOpen}
        title="Elimina modello"
        content={`Eliminare "${model.name}"? Questa azione non può essere annullata.`}
        onConfirm={handleConfirmDelete}
        onClose={() => setConfirmOpen(false)}
      />
    </>
  );
}

function ModelSubList({ projectId }: { projectId: string | number }) {
  const dataProvider = useDataProvider();
  const [models, setModels] = useState<ModelRecord[] | null>(null);
  const [loading, setLoading] = useState(false);

  function fetchModels() {
    setLoading(true);
    dataProvider
      .getList<ModelRecord>("models", {
        filter: { project_id: projectId },
        pagination: { page: 1, perPage: 200 },
        sort: { field: "name", order: "ASC" },
      })
      .then(({ data }) => setModels(data))
      .finally(() => setLoading(false));
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchModels(); }, [projectId]);

  if (loading && !models) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (!models || models.length === 0) {
    return (
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ px: 2, py: 2, fontStyle: "italic" }}
      >
        Nessun modello presente.
      </Typography>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          px: 2,
          py: 0.75,
          backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.04),
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ flex: "0 0 240px" }}>
          NOME
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ flex: 1, px: 2 }}>
          DESCRIZIONE
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ flex: "0 0 120px" }}>
          CREATO
        </Typography>
        <Box sx={{ width: 80 }} />
      </Box>
      {models.map((model) => (
        <ModelRow key={model.id} model={model} onDeleted={fetchModels} />
      ))}
    </Box>
  );
}

function ProjectRow({ record }: { record: ProjectRecord }) {
  const redirect = useRedirect();
  const navigate = useNavigate();
  const [deleteOne] = useDelete();
  const notify = useNotify();
  const { permissions } = usePermissions<string>();
  const [expanded, setExpanded] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  function handleEdit(e: React.MouseEvent) {
    e.stopPropagation();
    redirect("edit", "projects", record.id);
  }

  function handleAddModel(e: React.MouseEvent) {
    e.stopPropagation();
    navigate(`/models/create?project_id=${record.id}`);
  }

  function handleDeleteClick(e: React.MouseEvent) {
    e.stopPropagation();
    setConfirmOpen(true);
  }

  function handleConfirmDelete() {
    deleteOne(
      "projects",
      { id: record.id, previousData: record },
      {
        onSuccess: () => {
          notify("Progetto eliminato", { type: "success" });
        },
        onError: () => {
          notify(DELETE_ERROR_MESSAGE, { type: "error" });
        },
      }
    );
    setConfirmOpen(false);
  }

  return (
    <>
      <Accordion
        expanded={expanded}
        onChange={(_e, isExpanded) => setExpanded(isExpanded)}
        disableGutters
        elevation={0}
        sx={{
          position: "relative",
          backgroundColor: "background.paper",
          border: "1px solid",
          borderColor: (theme) => alpha(theme.palette.primary.main, 0.15),
          mb: 1,
          borderRadius: "6px !important",
          "&:before": { display: "none" },
          "&.Mui-expanded": {
            borderColor: (theme) => alpha(theme.palette.primary.main, 0.35),
          },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            px: 2,
            minHeight: 56,
            // Leave room on the right for the absolutely-positioned action
            // buttons (which can't live inside this <button> — invalid nesting)
            // and the expand chevron.
            "& .MuiAccordionSummary-content": {
              alignItems: "center",
              gap: 2,
              my: 0,
              pr: permissions === "admin" ? "140px" : "100px",
            },
            "&:hover": {
              backgroundColor: (theme) =>
                alpha(theme.palette.primary.main, 0.06),
            },
          }}
        >
          <Box sx={{ flex: "0 0 220px", minWidth: 0 }}>
            <Typography variant="body1" fontWeight={600} noWrap>
              {record.name}
            </Typography>
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" color="text.secondary" noWrap>
              {record.description ?? "—"}
            </Typography>
          </Box>
          <Box sx={{ flex: "0 0 120px" }}>
            <Typography variant="body2" color="text.secondary">
              {formatDate(record.created_at)}
            </Typography>
          </Box>
        </AccordionSummary>

        {/*
          Action buttons live OUTSIDE the AccordionSummary: that summary renders a
          <button>, and nesting <button>s inside it is invalid HTML. They are
          absolutely positioned over the summary row (left of the expand chevron).
        */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            right: 48,
            height: 56,
            display: "flex",
            alignItems: "center",
            gap: 0.5,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <Tooltip title="Modifica progetto">
            <IconButton aria-label="Modifica progetto" size="small" onClick={handleEdit}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Aggiungi modello">
            <IconButton aria-label="Aggiungi modello" size="small" onClick={handleAddModel} color="primary">
              <AddIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {permissions === "admin" && (
            <Tooltip title="Elimina progetto">
              <IconButton aria-label="Elimina progetto" size="small" onClick={handleDeleteClick}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        <Divider sx={{ borderColor: (theme) => alpha(theme.palette.primary.main, 0.15) }} />

        <AccordionDetails sx={{ p: 0 }}>
          {expanded && <ModelSubList projectId={record.id} />}
        </AccordionDetails>
      </Accordion>

      <Confirm
        isOpen={confirmOpen}
        title="Elimina progetto"
        content={`Eliminare "${record.name}"? Verranno eliminati il progetto e TUTTI i suoi modelli. Questa azione non può essere annullata.`}
        onConfirm={handleConfirmDelete}
        onClose={() => setConfirmOpen(false)}
      />
    </>
  );
}

function ProjectAccordionList() {
  const { data, isLoading } = useListContext<ProjectRecord>();

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Typography
        variant="body1"
        color="text.secondary"
        sx={{ textAlign: "center", py: 6, fontStyle: "italic" }}
      >
        Nessun progetto trovato. Crea il tuo primo progetto.
      </Typography>
    );
  }

  return (
    <Box sx={{ mt: 1 }}>
      {data.map((project) => (
        <ProjectRow key={project.id} record={project} />
      ))}
    </Box>
  );
}

function ListActions() {
  const { permissions } = usePermissions<string>();
  return (
    <TopToolbar>
      {permissions === "admin" && <CreateButton label="Crea progetto" />}
    </TopToolbar>
  );
}

export function ProjectList() {
  return (
    <List
      actions={<ListActions />}
      sort={{ field: "name", order: "ASC" }}
      pagination={false}
      perPage={200}
      component="div"
      empty={false}
    >
      <ProjectAccordionList />
    </List>
  );
}
